"use client";

import { useCallback, useRef, useState } from "react";
import type { StoredFileMeta } from "@/types/elearning";

/** Debe acompañar FILES_MAX_SIZE_MB del backend (que sigue siendo la autoridad). */
const MAX_UPLOAD_MB = 25;

/** Claves i18n por status HTTP del upload (el `message` del backend es prosa, no clave). */
const STATUS_ERROR_KEYS: Record<number, string> = {
  400: "fileEmptyOrMissing",
  401: "userNotAuth",
  413: "fileTooLarge",
  415: "fileTypeNotAllowed"
};

export interface FileUploadResult {
  file: StoredFileMeta | null;
  errorKey: string | null;
}

export interface FileUploadView {
  /** Sube el archivo vía el proxy same-origin `/api/files`. Ignora llamadas con otra subida en vuelo. */
  upload: (selected: File) => Promise<FileUploadResult>;
  uploading: boolean;
  /** Clave i18n del último error (null si la última subida fue exitosa). */
  errorKey: string | null;
  /** Metadatos del último archivo subido con éxito. */
  file: StoredFileMeta | null;
}

/**
 * @function useFileUpload
 * @description Sube archivos al módulo files del backend a través del proxy
 * same-origin `POST /api/files` (la cookie httpOnly de sesión viaja sola; el
 * browser no tiene tokens). Devuelve la URL interna (`/v2/files/{id}`) para
 * guardar en los campos de URL existentes. Los errores se exponen como claves
 * i18n mapeadas por status HTTP (413 tamaño, 415 tipo, 400 vacío, 401 sesión).
 *
 * @returns {FileUploadView} `upload` + estado de la subida en curso.
 */
const useFileUpload = (): FileUploadView => {
  const [uploading, setUploading] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [file, setFile] = useState<StoredFileMeta | null>(null);
  // Guard sincrónico contra dobles selecciones: el estado `uploading` llega
  // un render tarde, el ref corta la re-entrada en el mismo tick.
  const inFlight = useRef(false);

  const upload = useCallback(
    async (selected: File): Promise<FileUploadResult> => {
      if (inFlight.current) {
        return { file: null, errorKey: null };
      }
      if (selected.size > MAX_UPLOAD_MB * 1024 * 1024) {
        setErrorKey("fileTooLarge");
        return { file: null, errorKey: "fileTooLarge" };
      }

      inFlight.current = true;
      setUploading(true);
      setErrorKey(null);
      try {
        const formData = new FormData();
        formData.append("file", selected);
        const response = await fetch("/api/files", {
          method: "POST",
          body: formData
        });
        const payload = await response.json().catch(() => null);

        if (response.ok && payload?.data?.url) {
          const uploaded = payload.data as StoredFileMeta;
          setFile(uploaded);
          return { file: uploaded, errorKey: null };
        }

        const key = STATUS_ERROR_KEYS[response.status] ?? "fileUploadError";
        setErrorKey(key);
        return { file: null, errorKey: key };
      } catch {
        setErrorKey("fileUploadError");
        return { file: null, errorKey: "fileUploadError" };
      } finally {
        inFlight.current = false;
        setUploading(false);
      }
    },
    []
  );

  return { upload, uploading, errorKey, file };
};

export default useFileUpload;
